import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageBanner from '../components/PageBanner';
import Reveal from '../components/ui/Reveal';

const PILLARS = [
  {
    icon: 'fa-brain',
    title: 'Holistic Development',
    tag: 'Beyond Textbooks',
    description:
      'We believe in nurturing every dimension of a child — intellectual curiosity, emotional intelligence, physical fitness, creative expression, and ethical leadership.',
  },
  {
    icon: 'fa-user-graduate',
    title: 'Tailored & Adaptive Learning',
    tag: 'Personalized Focus',
    description:
      'Every child is uniquely gifted. Our small batch sizes and dedicated educator mentorship ensure that teaching adapts to each student’s pace and learning style.',
  },
  {
    icon: 'fa-school',
    title: 'State-of-the-Art Campus',
    tag: 'Modern Infrastructure',
    description:
      'Sprawling campus equipped with smart digital classrooms, advanced science and AI/computer labs, an expansive library, and professional sports arenas.',
  },
  {
    icon: 'fa-hands-holding-heart',
    title: 'Values & Character First',
    tag: 'Ethical Foundation',
    description:
      'Integrity, empathy, resilience, and compassion are woven into daily lessons, guiding students to make thoughtful choices and become principled global citizens.',
  },
  {
    icon: 'fa-award',
    title: 'Academic & Co-Curricular Balance',
    tag: 'Well-Rounded Growth',
    description:
      'A rigorous CBSE curriculum complemented by robust programs in music, dance, theatre, competitive sports, public speaking, and international assessments.',
  },
  {
    icon: 'fa-shield-halved',
    title: 'Safe & Nurturing Environment',
    tag: 'Student Well-Being',
    description:
      'Round-the-clock CCTV surveillance, compassionate pastoral care, trained healthcare personnel, and a supportive atmosphere where every learner belongs.',
  },
];

const WhyUs = () => {
  return (
    <>
      <SEO
        title="Why Choose St. Andrews World School, Sonipat | Best CBSE School"
        description="Discover why St. Andrews World School, Sonipat is the preferred choice for holistic education, world-class infrastructure, personalized care, and academic excellence."
        keywords="Why choose SAWS Sonipat, Best CBSE School in Sonipat, Top School Sonipat, St Andrews World School features"
      />

      {/* PAGE BANNER */}
      <PageBanner
        title="Why Choose St. Andrews World School?"
        subtitle="Empowering Minds, Inspiring Character, Shaping Tomorrow"
        breadcrumbs={[
          { label: 'Home', link: '/' },
          { label: 'Why Choose Us' },
        ]}
        fallback="/images/page-banners/why-choose-us.jpg"
      />


      {/* SECTION 1: OVERVIEW & PHILOSOPHY */}
      <section className="bg-white py-16 md:py-20">
        <div className="container lg:w-[75%] lg:max-w-[75%]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="order-2 lg:order-1 w-full text-left">
              <Reveal>
                <div className="mb-6 text-left">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#d8a85f] mb-2 block">
                    Excellence in Education
                  </span>
                  <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary leading-tight text-left">
                    More Than Just Academics — A Strong Foundation for Life
                  </h3>
                  <div className="w-16 h-1 bg-[#d8a85f] rounded-full mt-4" />
                </div>
              </Reveal>

              <Reveal delayMs={100}>
                <div className="space-y-4 text-body text-left leading-relaxed text-[16px]">
                  <p className="text-left font-semibold text-primary text-[17px] leading-relaxed">
                    At St. Andrews World School, Sonipat, we believe every child arrives with boundless potential waiting to be ignited.
                  </p>
                  <p className="text-left">
                    Our pedagogical philosophy moves far beyond the boundaries of standard textbooks. We provide a dynamic, student-centric ecosystem where intellectual curiosity is celebrated, questions are encouraged, and failures are treated as stepping stones to mastery.
                  </p>
                  <p className="text-left">
                    By blending the proven rigor of the CBSE curriculum with experiential learning, technological fluency, and dedicated faculty mentorship, we ensure that students do not simply memorize concepts — they understand, apply, and innovate.
                  </p>
                  <p className="text-left">
                    From foundational years through senior secondary schooling, we guide our students to develop resilience, empathy, independent thinking, and the confidence to excel anywhere in the world.
                  </p>
                </div>
              </Reveal>
            </div>

            {/* Right Visual Card */}
            <Reveal delayMs={70} className="order-1 lg:order-2">
              <div className="relative p-3 sm:p-4 border border-gray-200 bg-[#f9f8f4] shadow-lg rounded-2xl">
                <div className="aspect-[4/3] bg-gray-100 overflow-hidden rounded-xl">
                  <img
                    src="/images/page-banners/why-choose-us.jpg"
                    alt="Why Choose St Andrews World School"
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = '/images/aboutus.jpeg';
                    }}
                  />
                </div>
                <div className="absolute -bottom-5 -left-5 w-20 h-20 sm:w-24 sm:h-24 bg-primary hidden md:flex items-center justify-center rounded-full text-white border-4 border-white shadow-xl">
                  <i className="fas fa-graduation-cap text-2xl sm:text-3xl text-amber-400" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SECTION 2: SIX PILLARS OF DISTINCTION */}
      <section className="bg-[#f9f8f4] py-16 md:py-24">
        <div className="container">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <Reveal>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#d8a85f] mb-2 block text-center">
                The St. Andrews Advantage
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-primary leading-tight mb-3 text-center">
                What Sets Our Campus Apart
              </h2>
              <div className="w-16 h-1 bg-[#d8a85f] rounded-full mx-auto mb-5" />
              <p className="text-center text-body text-[16px] leading-relaxed max-w-2xl mx-auto">
                Six foundational strengths that make St. Andrews World School the leading educational destination for your child.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {PILLARS.map((pillar, idx) => (
              <Reveal key={idx} delayMs={idx * 60}>
                <div className="group h-full bg-white p-7 md:p-8 rounded-2xl border border-gray-200/90 hover:border-[#d8a85f]/50 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between hover:-translate-y-1 text-left">
                  <div className="text-left flex-1 flex flex-col">
                    {/* Header: Icon on left, Subtitle & Title on right */}
                    <div className="flex items-start gap-4 mb-4 min-h-[74px]">
                      <div className="w-12 h-12 rounded-xl bg-primary text-white flex items-center justify-center text-xl shadow-md group-hover:bg-[#d8a85f] transition-colors shrink-0 mt-0.5">
                        <i className={`fas ${pillar.icon}`} />
                      </div>

                      <div className="flex-1 text-left">
                        <span className="text-[11px] md:text-[12px] font-bold uppercase tracking-wider text-[#d8a85f] block mb-1">
                          {pillar.tag}
                        </span>
                        <h3 className="text-[18px] font-bold text-primary leading-snug group-hover:text-amber-800 transition-colors">
                          {pillar.title}
                        </h3>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-left text-body leading-relaxed text-[15px] flex-1">
                      {pillar.description}
                    </p>
                  </div>
                  <div className="mt-5 pt-3 border-t border-gray-200/60 flex items-center text-primary text-xs font-bold uppercase tracking-wider gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>Learn More</span>
                    <i className="fas fa-arrow-right text-[10px]" />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3: HOLISTIC EDUCATION & CO-CURRICULAR */}
      <section className="bg-white py-16 md:py-20">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Visual */}
            <Reveal delayMs={70} className="order-1">
              <div className="relative p-3 sm:p-4 border border-gray-200 rounded-2xl bg-[#f9f8f4] shadow-lg">
                <div className="aspect-[4/3] overflow-hidden rounded-xl bg-gray-100">
                  <img
                    src="/images/library.JPG"
                    alt="Active Learning and Discovery"
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = '/images/Computer-lab-scaled.jpg';
                    }}
                  />
                </div>
                <div className="absolute -bottom-5 -right-5 w-20 h-20 sm:w-24 sm:h-24 bg-primary hidden md:flex items-center justify-center rounded-full text-white border-4 border-white shadow-xl">
                  <i className="fas fa-award text-2xl sm:text-3xl text-[#d8a85f]" />
                </div>
              </div>
            </Reveal>

            {/* Content */}
            <div className="order-2 w-full text-left">
              <Reveal>
                <div className="mb-5 text-left">
                  <span className="text-xs font-bold uppercase tracking-widest text-[#d8a85f] mb-2 block">
                    Cultivating Tomorrow's Leaders
                  </span>
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary leading-tight mb-3 text-left">
                    Experiential Learning, Sports & Creative Arts
                  </h2>
                  <div className="w-16 h-1 bg-[#d8a85f] rounded-full mt-3 mb-5" />
                </div>
                <div className="space-y-4 text-body text-[15px] sm:text-[16px] leading-relaxed text-left">
                  <p className="text-left">
                    We recognize that confidence and leadership take root far beyond exam halls. Through football, cricket, basketball, taekwondo, swimming, and track athletics, students cultivate fitness, teamwork, resilience, and true sportsmanship.
                  </p>
                  <p className="text-left">
                    Creative spirits thrive across dedicated performing arts studios for music, classical and western dance, theatre, and visual fine arts.
                  </p>
                  <p className="text-left">
                    Our coding, technology, and STEM enrichment modules prepare students to be proactive problem-solvers in an artificial intelligence-driven world.
                  </p>
                </div>
                <div className="mt-8 flex flex-wrap gap-3.5">
                  <Link
                    to="/our-programmes"
                    className="px-6 py-2.5 bg-primary hover:bg-primary-dark text-white font-bold text-xs uppercase tracking-widest rounded-full transition-colors shadow-md"
                  >
                    Our Programmes
                  </Link>
                  <Link
                    to="/other-facilities"
                    className="px-6 py-2.5 border-2 border-primary text-primary hover:bg-primary hover:text-white font-bold text-xs uppercase tracking-widest rounded-full transition-colors"
                  >
                    Explore Facilities
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default WhyUs;